// ─── Palette discipline ─────────────────────────────────────────────────────
// Ink by default. ONE muted tone for the direct-identifier class at rest.
// ONE accent, reserved for state a step is actively doing to a field
// (flagged as high risk, newly suppressed, newly inferred) — never a
// standing per-field category color. See StickyFingerprint.jsx and
// FingerprintGrid.jsx, which import these same constants so every view
// of the fingerprint stays in sync.

// These are drawn ON the aubergine ground (--cream: #2A1A24), so they track
// the CSS palette tokens: INK = --ink, ACCENT = --red. They were left on the
// pre-redesign light-theme values (near-black ink, a true red accent), which
// desktop ridges never revealed because they hardcode stroke="var(--ink)" —
// but the mobile bars and the crowd grid read these directly, so the bars
// rendered near-black on near-black and the grid flashed the old red.
export const INK = '#F3EFE6';
export const IDENTIFIER_GRAY = '#8a8075';   // shared tone, direct identifiers at rest (name/dob/ssn)
export const ACCENT = '#E8C872';             // reserved for step-driven state only

export const BASE_BARS = [
  {
    id: 'name', label: 'Full Name', section: 'demographic', baseWidth: 85,
    color: IDENTIFIER_GRAY,
    description: 'Your legal name as recorded across hospital and clinic systems.',
    why: 'Used to match your record to trial consent forms and link visits over time. Almost always removed before data is shared for research.',
    stepNotes: {
      4: 'The most obvious identifier — removed first. But your name being gone doesn\'t mean you\'re gone. Everything else stays.',
    },
  },
  {
    id: 'dob', label: 'Date of Birth', section: 'demographic', baseWidth: 85,
    color: IDENTIFIER_GRAY,
    description: 'Your exact date of birth as recorded in your medical record.',
    why: 'Used to confirm patient identity and calculate age-based eligibility criteria for the trial.',
  },
  {
    id: 'ssn', label: 'Social Security #', section: 'demographic', baseWidth: 85,
    color: IDENTIFIER_GRAY,
    description: 'Your government-issued national identifier.',
    why: 'Used for identity verification and insurance billing during enrollment. Removed before any research dataset is shared.',
    stepNotes: {
      4: 'Removed before your data leaves the hospital. Your SSN is the clearest link between your medical record and your legal identity — so it\'s the first to go.',
    },
  },
  {
    id: 'zip', label: 'ZIP Code', section: 'demographic', baseWidth: 85,
    color: INK,
    description: 'Your 5-digit postal code, retained as a geographic marker.',
    why: 'Retained as a quasi-identifier to study geographic health patterns and socioeconomic disparities — but it\'s also one of the fields most useful to an attacker.',
    stepNotes: {
      4: 'Your full address is removed, but ZIP survives — generalized to a 3-digit prefix. Precise enough to study geography. Usually vague enough to protect you.',
      7: 'Your ZIP region survived de-identification. Combined with your age bracket and diagnosis, it may be enough for someone to find you in a public dataset.',
    },
  },
  {
    id: 'diagnosis', label: 'Diagnosis Codes', section: 'clinical', baseWidth: 85,
    color: INK,
    description: 'ICD-10 codes describing your documented medical conditions.',
    why: 'Defines trial eligibility and helps researchers study how the intervention performs across different disease subgroups.',
    stepNotes: {
      4: 'Generalized — specific codes are replaced with broader categories. "Type 2 diabetes, poorly controlled" becomes just "diabetes."',
      6: 'The pattern emerges here. Your diagnosis codes, crossed with lab trends and wearable signals, reveal how patients like you respond to treatment.',
      7: 'A rare diagnosis can make you uniquely identifiable even in a stripped dataset. The rarer your condition, the smaller your crowd.',
    },
  },
  {
    id: 'notes', label: 'Clinical Notes', section: 'clinical', baseWidth: 85,
    color: INK,
    description: 'Free-text observations written by your care team.',
    why: 'Contain nuanced clinical context that structured codes miss. AI uses natural language processing to screen these notes for eligibility.',
    stepNotes: {
      1: 'AI reads these notes using Natural Language Processing — finding eligibility clues that structured codes miss, like a passing mention of a symptom or a dosage change.',
      4: 'Removed entirely before sharing. Free-text notes are too rich in incidental detail — names, relationships, locations — to safely generalize.',
    },
  },
  {
    id: 'meds', label: 'Medications', section: 'clinical', baseWidth: 85,
    color: INK,
    description: 'The full list of prescription and over-the-counter drugs in your record.',
    why: 'Screens for drug interactions with the trial intervention and helps characterize the patient population being studied.',
  },
  {
    id: 'labs', label: 'Lab Results', section: 'measurement', baseWidth: 85,
    color: INK,
    description: 'Blood panels, urine tests, and other clinical measurements.',
    why: 'Establishes your baseline health status and tracks how your body responds to the trial intervention over time.',
    stepNotes: {
      4: 'Noise is added — your exact values shift slightly. Your real cholesterol was 187; the dataset records 184. Close enough for statistics, far enough to break an exact match.',
    },
  },
  {
    id: 'wearable', label: 'Wearable Data', section: 'measurement', baseWidth: 85,
    color: INK,
    description: 'Continuous sensor data from fitness trackers or medical-grade wearables.',
    why: 'Captures real-world activity, sleep, and physiological patterns outside of scheduled clinical visits — data that didn\'t exist in your record before you enrolled.',
    stepNotes: {
      3: 'This bar didn\'t exist before you enrolled. Your wearable, now linked to your trial record, streams data that no one collected from you before you signed the consent form.',
      6: 'Continuous wearable signals — steps, heart rate, sleep — give researchers a window into your daily life that scheduled clinic visits can\'t capture.',
    },
  },
];

export const NEW_BARS = [
  {
    id: 'trial_treatment', label: 'Trial Treatment', section: 'new', baseWidth: 85,
    color: INK,
    description: 'The experimental treatment or placebo you receive during the trial.',
    why: 'Links your identity to your assigned arm — the core variable the trial is designed to study.',
    stepNotes: {
      3: 'Recorded from the moment you\'re assigned to a treatment arm. This field links your identity to whether you received the drug or the placebo.',
    },
  },
];

export const INFERRED_BARS = [
  {
    id: 'inferred_income', label: 'Inferred: Income Bracket', section: 'inferred', baseWidth: 85,
    color: ACCENT,
    description: 'Not collected — predicted by AI from prescription patterns and geographic data.',
    why: 'A model trained on medication history and ZIP region can estimate socioeconomic status without ever asking.',
  },
];

export const SUPPRESSED_IDS  = ['name', 'ssn', 'notes'];
export const GENERALIZED_IDS = ['dob', 'zip', 'diagnosis'];
export const NOISE_IDS       = ['labs', 'wearable'];
export const PATTERN_A_ID    = 'diagnosis';
export const PATTERN_B_ID    = 'wearable';
export const REID_IDS        = ['zip', 'diagnosis', 'meds'];

// Fixed field order used to assign each field a stable ridge radius in the
// concentric fingerprint rendering, independent of which subset is visible
// at a given step (so a field's ridge size never jumps around).
export const RIDGE_ORDER = [
  'name', 'dob', 'ssn', 'zip', 'diagnosis', 'notes', 'meds', 'labs',
  'wearable', 'trial_treatment', 'inferred_income',
];
